import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  endOfMonth,
  endOfQuarter,
  endOfWeek,
  endOfYear,
  format,
  subDays,
  startOfMonth,
  startOfQuarter,
  startOfToday,
  startOfWeek,
  startOfYear
} from "date-fns";
import useDashboardStore, { DEFAULT_FILTERS } from "@/stores/dashboardStore";

export const DASHBOARD_FILTER_STORAGE_KEY = "ivoliq_dashboard_filters";

export const getPresetRange = (preset, referenceDate = new Date()) => {
  switch (preset) {
    case "today":
      return {
        dateFrom: format(startOfToday(), "yyyy-MM-dd"),
        dateTo: format(startOfToday(), "yyyy-MM-dd")
      };
    case "this-week":
      return {
        dateFrom: format(startOfWeek(referenceDate, { weekStartsOn: 1 }), "yyyy-MM-dd"),
        dateTo: format(endOfWeek(referenceDate, { weekStartsOn: 1 }), "yyyy-MM-dd")
      };
    case "this-quarter":
      return {
        dateFrom: format(startOfQuarter(referenceDate), "yyyy-MM-dd"),
        dateTo: format(endOfQuarter(referenceDate), "yyyy-MM-dd")
      };
    case "this-year":
      return {
        dateFrom: format(startOfYear(referenceDate), "yyyy-MM-dd"),
        dateTo: format(endOfYear(referenceDate), "yyyy-MM-dd")
      };
    case "last-30-days":
      return {
        dateFrom: format(subDays(referenceDate, 29), "yyyy-MM-dd"),
        dateTo: format(startOfToday(), "yyyy-MM-dd")
      };
    case "this-month":
    default:
      return {
        dateFrom: format(startOfMonth(referenceDate), "yyyy-MM-dd"),
        dateTo: format(endOfMonth(referenceDate), "yyyy-MM-dd")
      };
  }
};

export const buildQueryFromFilters = (filters) => {
  const params = new URLSearchParams();
  if (filters.projectIds?.length) {
    params.set("project_ids", filters.projectIds.join(","));
  }
  if (filters.customerIds?.length) {
    params.set("customer_ids", filters.customerIds.join(","));
  }
  if (filters.dateFrom) {
    params.set("date_from", filters.dateFrom);
  }
  if (filters.dateTo) {
    params.set("date_to", filters.dateTo);
  }
  if (filters.datePreset) {
    params.set("preset", filters.datePreset);
  }
  if (filters.status && filters.status !== "all") {
    params.set("status", filters.status);
  }
  const queryString = params.toString();
  return queryString ? `?${queryString}` : "";
};

export const parseFiltersFromQuery = (search) => {
  const params = new URLSearchParams(search);
  const output = {};

  if (params.has("project_ids")) {
    const projectIds = params.get("project_ids")?.split(",").filter(Boolean) || [];
    output.projectIds = projectIds;
  }
  if (params.has("customer_ids")) {
    const customerIds = params.get("customer_ids")?.split(",").filter(Boolean) || [];
    output.customerIds = customerIds;
  }
  if (params.has("date_from")) {
    output.dateFrom = params.get("date_from") || "";
  }
  if (params.has("date_to")) {
    output.dateTo = params.get("date_to") || "";
  }
  if (params.has("preset")) {
    output.datePreset = params.get("preset") || "";
  }
  if (params.has("status")) {
    output.status = params.get("status") || "all";
  }

  return output;
};

export const loadFiltersFromStorage = () => {
  try {
    const raw = localStorage.getItem(DASHBOARD_FILTER_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (error) {
    return null;
  }
};

export const saveFiltersToStorage = (filters) => {
  try {
    localStorage.setItem(DASHBOARD_FILTER_STORAGE_KEY, JSON.stringify(filters));
  } catch (error) {
    // ignore storage errors
  }
};

export default function useDashboardFilters() {
  const location = useLocation();
  const navigate = useNavigate();
  const { filters, setFilters, resetFilters, isHydrated, setHydrated } = useDashboardStore();
  const [debouncedFilters, setDebouncedFilters] = useState(filters);
  const hasInitialized = useRef(false);

  useEffect(() => {
    if (isHydrated || hasInitialized.current) return;
    const storedFilters = loadFiltersFromStorage();
    const queryFilters = parseFiltersFromQuery(location.search);
    const merged = {
      ...DEFAULT_FILTERS,
      ...storedFilters,
      ...queryFilters
    };

    if (merged.invoiceType) {
      delete merged.invoiceType;
    }

    const preset = merged.datePreset || DEFAULT_FILTERS.datePreset;
    if (!merged.dateFrom || !merged.dateTo) {
      const range = getPresetRange(preset);
      merged.dateFrom = range.dateFrom;
      merged.dateTo = range.dateTo;
      merged.datePreset = preset;
    }

    setFilters(merged);
    setHydrated(true);
    hasInitialized.current = true;
  }, [isHydrated, location.search, setFilters, setHydrated]);

  useEffect(() => {
    if (!isHydrated) return;
    const query = buildQueryFromFilters(filters);
    if (query !== location.search) {
      navigate({ search: query }, { replace: true });
    }
    saveFiltersToStorage(filters);
  }, [filters, isHydrated, location.search, navigate]);

  useEffect(() => {
    if (!isHydrated) return;
    const handler = setTimeout(() => {
      setDebouncedFilters(filters);
    }, 300);
    return () => clearTimeout(handler);
  }, [filters, isHydrated]);

  const setPreset = (preset) => {
    const range = getPresetRange(preset);
    setFilters({
      datePreset: preset,
      dateFrom: range.dateFrom,
      dateTo: range.dateTo
    });
  };

  const setCustomRange = (range) => {
    setFilters({
      datePreset: "custom",
      dateFrom: range.dateFrom,
      dateTo: range.dateTo
    });
  };

  const resetAll = () => {
    const range = getPresetRange(DEFAULT_FILTERS.datePreset);
    resetFilters();
    setFilters({
      datePreset: DEFAULT_FILTERS.datePreset,
      dateFrom: range.dateFrom,
      dateTo: range.dateTo
    });
  };

  const hasActiveFilters = useMemo(() => {
    return (
      filters.projectIds.length > 0 ||
      filters.customerIds.length > 0 ||
      filters.status !== "all" ||
      filters.datePreset === "custom"
    );
  }, [filters]);

  return {
    filters,
    debouncedFilters,
    isHydrated,
    setFilters,
    setPreset,
    setCustomRange,
    resetFilters: resetAll,
    hasActiveFilters
  };
}